<script setup>
import { computed, ref, watch } from 'vue'
import { ChevronDown } from 'lucide-vue-next'
import {
    Dialog as UIDialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import AdvancedFilterFields from '@/partials/options/AdvancedFilterFields.vue'

const props = defineProps({
    open: {
        type: Boolean,
        default: false,
    },
    fieldLabel: {
        type: String,
        default: '',
    },
    itemLabel: {
        type: String,
        default: '',
    },
    specifics: {
        type: Array,
        default: () => [],
    },
    tempValues: {
        type: Object,
        default: () => ({}),
    },
})

const emit = defineEmits([
    'update:open',
    'update:tempValues',
    'cancel',
    'apply',
    'validate-input-key-press',
])

const title = computed(() => `${props.itemLabel || 'Selected item'} advanced requirements`)

const description = computed(() => (
    `Applies to the creator's content in relation to "${props.fieldLabel}" - "${props.itemLabel}".`
))

const advancedFiltersOpen = ref(false)

const primarySpecifics = computed(() => props.specifics.filter((specific) => specific.isAdvanced !== true))

const advancedSpecifics = computed(() => props.specifics.filter((specific) => specific.isAdvanced === true))

const hasValueForSpecific = (specificItem) => {
    const value = props.tempValues?.[specificItem.key]

    if (specificItem.type === 'boolean') return value === true
    if (!value || typeof value !== 'object') return false

    return [value.min, value.max].some((bound) => bound !== null && bound !== undefined && bound !== '')
}

const hasAdvancedValues = computed(() => advancedSpecifics.value.some(hasValueForSpecific))

watch(
    () => props.open,
    (isOpen) => {
        if (isOpen) {
            advancedFiltersOpen.value = hasAdvancedValues.value
        }
    },
)
</script>

<template>
    <UIDialog :open="open" @update:open="value => emit('update:open', value)">
        <DialogContent class="max-w-2xl">
            <DialogHeader>
                <DialogTitle>{{ title }}</DialogTitle>
                <DialogDescription>
                    {{ description }}
                </DialogDescription>
            </DialogHeader>

            <div class="space-y-4 py-2">
                <AdvancedFilterFields
                    :specifics="primarySpecifics"
                    :temp-values="tempValues"
                    @update:temp-values="value => emit('update:tempValues', value)"
                    @validate-input-key-press="(event, specificItem) => emit('validate-input-key-press', event, specificItem)"
                />

                <Collapsible
                    v-if="advancedSpecifics.length"
                    v-model:open="advancedFiltersOpen"
                    class="rounded-md border border-slate-200"
                >
                    <CollapsibleTrigger as-child>
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            class="w-full justify-between px-3 text-slate-600"
                        >
                            <span>{{ advancedFiltersOpen ? 'Hide advanced filters' : 'Show advanced filters' }}</span>
                            <ChevronDown
                                class="h-4 w-4 transition-transform"
                                :class="{ 'rotate-180': advancedFiltersOpen }"
                            />
                        </Button>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                        <div class="border-t border-slate-100 p-3">
                            <AdvancedFilterFields
                                :specifics="advancedSpecifics"
                                :temp-values="tempValues"
                                @update:temp-values="value => emit('update:tempValues', value)"
                                @validate-input-key-press="(event, specificItem) => emit('validate-input-key-press', event, specificItem)"
                            />
                        </div>
                    </CollapsibleContent>
                </Collapsible>
            </div>

            <DialogFooter>
                <Button type="button" variant="outline" @click="emit('cancel')">
                    Cancel
                </Button>
                <Button type="button" @click="emit('apply')">
                    Apply
                </Button>
            </DialogFooter>
        </DialogContent>
    </UIDialog>
</template>
